import { memo, useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Button } from 'components/common';
import { Icon } from 'components/common/icons';
import { useEditorDispatch } from 'components/editor';
import { editorSocket } from 'components/editor/socket';
import {
  EditorHeaderSelectedBlocksCopy,
  EditorHeaderSelectedBlocksMarkAsNew
} from '.';

import type { EditorHeaderStateProps } from './EditorHeader';

type StateProps = Pick<
  EditorHeaderStateProps,
  | 'chapters'
  | 'currentChapter'
  | 'isCopyingBlocks'
  | 'isCopyingBlocksSuccess'
  | 'isMoving'
>;

export type EditorHeaderSelectedBlocksProps = StateProps & {
  actionIsHide: boolean;
  selectedBlockIds: number[];
};

const EditorHeaderSelectedBlocks = ({
  actionIsHide,
  currentChapter,
  isMoving,
  selectedBlockIds,
  ...stateProps
}: EditorHeaderSelectedBlocksProps) => {
  const editorDispatch = useEditorDispatch();

  const { messages, theme } = useReduxState();

  const [isDeleteView, setIsDeleteView] = useState(false);

  useEffect(() => {
    if (isDeleteView && !selectedBlockIds.length) {
      setIsDeleteView(false);
    }
  }, [isDeleteView, selectedBlockIds]);

  const handleClear = () => {
    editorSocket.clearSelectedBlocks();
  };

  const handleDelete = () => {
    editorSocket.deleteBlocks({
      blockIds: selectedBlockIds,
      chapterId: currentChapter.id
    });
  };

  const handleDeleteToggle = () => {
    setIsDeleteView(!isDeleteView);
  };

  const handleHide = (ev) => {
    const visibility = ev.currentTarget.getAttribute('data-visibility');
    editorSocket.hideBlocks({
      blockIds: selectedBlockIds,
      chapterId: currentChapter.id,
      visibility
    });
  };

  const handleMove = () => {
    editorDispatch({
      type: 'updateContext',
      update: { editorTransferBlocksVisible: true }
    });
  };

  return !selectedBlockIds.length ? null : (
    <div className="selected">
      <div className="status">
        <div className="info">
          <div className="icon">
            <Icon icon="check" />
          </div>

          <div className="count">{selectedBlockIds.length}</div>

          <div className="label">
            {isMoving
              ? messages.editorHeaderBlocksMoveSelected
              : messages.editorHeaderBlocksSelected}
          </div>
        </div>

        <Button
          category="lighter"
          icon="times"
          onClick={handleClear}
          text={messages.commonCancel}
          size="small"
        />
      </div>

      {!isMoving ? (
        <div className="actions">
          {isDeleteView ? (
            <>
              <Button
                category="danger"
                icon="trash-alt"
                onClick={handleDelete}
                size="small"
                style={{ marginRight: 5 }}
                text={messages.commonDelete}
              />

              <Button
                category="lighter"
                onClick={handleDeleteToggle}
                size="small"
                text={messages.commonCancel}
              />
            </>
          ) : (
            <>
              <EditorHeaderSelectedBlocksCopy
                chapters={stateProps.chapters}
                currentChapter={currentChapter}
                isCopyingBlocks={stateProps.isCopyingBlocks}
                isCopyingBlocksSuccess={stateProps.isCopyingBlocksSuccess}
                selectedBlockIds={selectedBlockIds}
              />

              <Button
                aria-label={messages.editorHeaderTransferBlocks}
                data-tooltip={messages.editorHeaderTransferBlocks}
                category="light"
                icon="share"
                onClick={handleMove}
                size="small"
                style={{ marginRight: 10 }}
              />

              <EditorHeaderSelectedBlocksMarkAsNew
                currentChapter={currentChapter}
                selectedBlockIds={selectedBlockIds}
              />

              <Button
                aria-label={
                  actionIsHide
                    ? messages.editorHeaderHideBlocks
                    : messages.editorHeaderUnhideBlocks
                }
                data-tooltip={
                  actionIsHide
                    ? messages.editorHeaderHideBlocks
                    : messages.editorHeaderUnhideBlocks
                }
                data-visibility={actionIsHide ? 'hidden' : 'visible'}
                category="light"
                icon={actionIsHide ? 'eye-slash' : 'eye'}
                onClick={handleHide}
                size="small"
                style={{ marginRight: 10 }}
              />

              <Button
                aria-label={messages.editorHeaderDeleteBlocks}
                data-tooltip={messages.editorHeaderDeleteBlocks}
                category="light"
                icon="trash-alt"
                onClick={handleDeleteToggle}
                size="small"
              />
            </>
          )}
        </div>
      ) : null}

      <style jsx>{`
        .selected {
          border-top: 1px solid ${theme.lightGreyDark};
          padding: 10px 15px;
        }
        .info {
          color: ${theme.textColor};
          display: inline-block;
          margin-right: 15px;
          padding-left: 2px;
          position: relative;
          top: 1px;
        }
        .icon {
          background-color: ${theme.successColor};
          border-radius: 4px;
          color: ${theme.successTextColor};
          display: inline-block;
          font-size: 0.8rem;
          height: 18px;
          margin-right: 7px;
          padding: 4px 0;
          position: relative;
          text-align: center;
          top: -2px;
          width: 18px;
        }
        .count {
          display: inline-block;
          font-size: 1.5rem;
          font-weight: 700;
          margin-right: 5px;
        }
        .label {
          color: ${theme.textColorLight};
          display: inline-block;
          font-size: 1.4rem;
          font-weight: 600;
        }
        @media (max-width: ${breakpoints.small - 1}px) {
          .actions {
            margin-top: 5px;
          }
        }
        @media (min-width: ${breakpoints.small}px) {
          .selected {
            align-items: center;
            display: flex;
          }
          .status {
            flex-grow: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default memo(EditorHeaderSelectedBlocks);
